import React, { useEffect, useRef } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import type { AskQuestionItem } from '../../store/chatStore';

import { useTranslation } from 'react-i18next';

interface AskQuestionInput {
  questions?: AskQuestionItem[];
}

interface AskQuestionContentProps {
  content: ToolUseBlock;
  isLastMessage?: boolean;
}

export const AskQuestionContent: React.FC<AskQuestionContentProps> = ({
  content,
  isLastMessage = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  const input = (content.input || {}) as AskQuestionInput;
  const questions = Array.isArray(input.questions) ? input.questions : [];

  const setActiveAskQuestion = useChatStore((s) => s.setActiveAskQuestion);
  const activeAskQuestion = useChatStore((s) => s.activeAskQuestion);
  const wasActiveRef = useRef(false);

  // Stringify so the effect re-runs whenever options/labels change during streaming,
  // not just when the number of questions changes. Computed here (before the effect)
  // so it's stable as a dependency.
  const questionsKey = questions.length > 0 ? JSON.stringify(questions) : '';

  useEffect(() => {
    if (!isLastMessage || questions.length === 0) return;
    wasActiveRef.current = true;
    setActiveAskQuestion({ questions });
    return () => {
      setActiveAskQuestion(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastMessage, questionsKey, setActiveAskQuestion]);

  if (questions.length === 0) return null;

  const isActive = isLastMessage && activeAskQuestion !== null;
  const isCompleted = wasActiveRef.current && !isActive;

  if (isActive) {
    return (
      <div className="my-2 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-400 dark:border-gray-700">
          <span className="bg-action-primary h-1.5 w-1.5 animate-pulse rounded-full" />
          {t('questionsBelow', { count: questions.length })}
        </span>
      </div>
    );
  }

  if (isCompleted || !isLastMessage) {
    return (
      <div className="my-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800">
          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
          {t('questionsAsked', { count: questions.length })}
        </span>
      </div>
    );
  }

  return null;
};
